import { MODULE_TYPE_LABELS } from "entities/modules/constants";
import { PageModuleData } from "entities/pages/types";
import { MouseEvent, useMemo, useState } from "react";
import { FilterOption, FilterProps } from "shared/components/Filter";
import { useAnchorEl } from "shared/hooks/useAnchorEl";

type FilterKey = "type";

type FilterConfig = {
  key: FilterKey;
  label: string;
  sortAlphabetically?: boolean;
};

export type PageModulesFilterHook = {
  filtersData: FilterProps[];
  filteredModules: PageModuleData[];
  searchValue: string;
  setSearchValue: (value: string) => void;
};

const filterConfigs: FilterConfig[] = [
  {
    key: "type",
    label: "Module Type",
    sortAlphabetically: true,
  },
];

export const usePageModulesFilter = (
  modules: PageModuleData[],
): PageModulesFilterHook => {
  const [selectedValues, setSelectedValues] = useState<{
    [key in FilterKey]?: FilterOption[];
  }>({});
  const [openStates, setOpenStates] = useState<{
    [key in FilterKey]?: boolean;
  }>({});
  const { anchorEl, handleAnchorClose, handleAnchorOpen } = useAnchorEl();
  const [searchValue, setSearchValue] = useState<string>("");

  const filterOptions = useMemo(() => {
    const typeSet = new Set<string>(
      modules.map((module) => module.type).filter(Boolean),
    );

    const typeOptions = Array.from(typeSet)
      .filter(
        (type) => MODULE_TYPE_LABELS[type as keyof typeof MODULE_TYPE_LABELS],
      )
      .map((type) => ({
        value: type,
        label: MODULE_TYPE_LABELS[type as keyof typeof MODULE_TYPE_LABELS],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return {
      type: typeOptions,
    };
  }, [modules]);

  const filtersData: FilterProps[] = filterConfigs.map(
    ({ key, label, sortAlphabetically }) => {
      const options = filterOptions[key] || [];

      const handleChangeValue = (
        _: MouseEvent<HTMLElement>,
        option: FilterOption,
      ) => {
        setSelectedValues((prev) => {
          const currentOptions = prev[key] || [];
          const isOptionSelected = currentOptions.some(
            (o) => o.value === option.value,
          );

          const newValues = isOptionSelected
            ? currentOptions.filter((o) => o.value !== option.value)
            : [...currentOptions, option];

          return {
            ...prev,
            [key]: newValues,
          };
        });
      };

      const onClearSelectedValues = () =>
        setSelectedValues((prev) => ({ ...prev, [key]: [] }));

      const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setOpenStates((prev) => ({ ...prev, [key]: true }));
        handleAnchorOpen(event);
      };

      const handleClose = () => {
        setOpenStates((prev) => ({ ...prev, [key]: false }));
        handleAnchorClose();
      };

      return {
        title: label,
        options,
        anchorEl,
        open: openStates[key] || false,
        selectedValue: selectedValues[key] || [],
        handleOpenMenu,
        handleChangeValue,
        handleClose,
        onClearSelectedValues,
        disabled: false,
        sortAlphabetically,
      };
    },
  );

  const filteredModules = useMemo(() => {
    return modules.filter((module) => {
      const typeMatches =
        selectedValues.type?.length === 0 ||
        selectedValues.type?.some((type) => type.value === module.type);

      const nameMatches =
        !searchValue ||
        module.name?.toLowerCase().includes(searchValue.toLowerCase());

      return typeMatches && nameMatches;
    });
  }, [modules, selectedValues, searchValue]);

  return {
    filtersData,
    filteredModules,
    searchValue,
    setSearchValue,
  };
};
